/*
 *   File : order-listing.js
 *   Author : https://evoqins.com
 *   Description : Index file to import components
 *   Integrations : 
 *   Version : 1.0.0
*/

import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

// Components
import { TextButton } from "../../Component/Buttons";
import { CustomSearchInput } from "../../Component/FormElements";
import { Icon } from "../../Component/Icon";
import { AlphaBasketCard } from "../../Component/Cards";
import { EmptyScreen, Loader, OverLay } from "../../Component/Others";
import { OrderFilterModal, OrdersSortModal } from "../../Component/Modal";
import Overlay from "../../Component/Others/overlay";
import { APIService } from "../../Service";
import { _scrollToTop } from "../../Helper";


// Assets
import EmptyOrder from '../../Assets/Images/EmptyScreen/empty-order.webp';

// Custom data

const SORT_OPTIONS = [
    {
        id: "first_invested_on",
        label: "First Order First",
    },
    {
        id: "last_invested_on",
        label: "First Order Last"
    },
    {
        id: "product_name_asc",
        label: "Product Name Asc"
    },
    {
        id: "product_name_desc",
        label: "Product Name Desc"
    },
];

const ORDER_TYPE = [
    {
        id: 1,
        label: "SIP",
    },
    {
        id: 2,
        label: "Lumpsum"
    },

];

const ORDER_STATUS = [
    {
        id: "Success",
        label: "Success",
    },
    {
        id: "Partially Completed",
        label: "Pending"
    },
    {
        id: "Failed",
        label: "Failed"
    },
];

const PAYMENT_STATUS = [
    {
        id: 1,
        label: "Success",
    },
    {
        id: 2,
        label: "Processing"
    },
    {
        id: 3,
        label: "Failed"
    },
];


function OrderListing() {

    const scrollRef = useRef(null);
    const navigate = useNavigate();
    const USER_DATA = useSelector((store) => store.Reducer.USER_DATA);
    const initialRender = useRef(true);

    const [orderList, setOrderList] = useState([]);

    // Sort
    const [searchValue, setSearchValue] = useState("");
    const [selectedSort, setSelectedSort] = useState(SORT_OPTIONS[0].id);
    const [toggleSort, setToggleSort] = useState(false);
    // Filter
    const [toggleFilter, setToggleFilter] = useState(false);
    const [selectedOrderTypeId, setSelectedOrderTypeId] = useState([]);
    const [selectedOrderStatusId, setSelectedOrderStatusId] = useState([]);
    const [selectedPaymentStatusId, setSelectedPaymentStatusId] = useState([]);
    const [dataLoading, setDataLoading] = useState(true);

    useEffect(() => {
        // Scroll the page to top
        _scrollToTop();

        // API - Getting order list data
        _getOrderList();
    }, []);

    useEffect(() => {
        if (searchValue.length === 0 && initialRender.current === false) {
            setDataLoading(true);
            // API - Getting order list data
            _getOrderList();
        }
    }, [searchValue]);



    useEffect(() => {
        if (toggleFilter === true || toggleSort === true) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
    }, [toggleFilter, toggleSort]);

    // handle search 
    const _handleSearch = (value) => {
        setSearchValue(value);
    };

    // handle sort toggle modal 
    const _handleSortToggle = () => {
        setToggleSort(!toggleSort)
        if (scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    //handle select sort
    const _handleSelectSort = (id) => {
        setSelectedSort(id);
    };

    // handle clear selected sort option 
    const _handleClearSort = () => {
        setSelectedSort(null);
    };

    // handle cancel sort 
    const _handleCancelSort = () => {
        setToggleSort(false);
        setSelectedSort(null);
    };

    // handle apply sort 
    const _handleApplySort = () => {
        setToggleSort(false);
        setDataLoading(true);
        _getOrderList();

    };

    // handle cancel filter 
    const _handleCancelFilter = () => {
        setToggleFilter(false);
    };

    // handle clear filter 
    const _handleClearFilter = () => {
        // setSelectedOrderTypeId([]);
        setSelectedOrderStatusId(null);
        // setSelectedPaymentStatusId([]);
    };

    // handle apply filter 
    const _handleApplyFilter = () => {
        setToggleFilter(false);
        setDataLoading(true);
        _getOrderList();
    };

    // handle Select Order Type
    const _handleSelectOrderType = (id) => {
        if (selectedOrderTypeId.includes(id)) {
            setSelectedOrderTypeId(selectedOrderTypeId.filter(order_type_id => order_type_id !== id));
        } else {
            setSelectedOrderTypeId([...selectedOrderTypeId, id]);
        }
    };

    // handle Select Order Status
    const _handleSelectOrderStatus = (id) => {
        setSelectedOrderStatusId(id);
        // if (selectedOrderStatusId.includes(id)) {
        //     setSelectedOrderStatusId(selectedOrderStatusId.filter(order_status_id => order_status_id !== id));
        // } else {
        //     setSelectedOrderStatusId([...selectedOrderStatusId, id]);
        // }
    };

    // handle Select payment Status
    const _handleSelectPaymentStatus = (id) => {
        if (selectedPaymentStatusId.includes(id)) {
            setSelectedPaymentStatusId(selectedPaymentStatusId.filter(payment_status_id => payment_status_id !== id));
        } else {
            setSelectedPaymentStatusId([...selectedPaymentStatusId, id]);
        }
    };

    // handle toggle filter 
    const _handleToggleFilter = () => {
        setToggleFilter((toggle_filter) => !toggle_filter);
    };

    // handle navigate to explore page
    const _handleStartInvesting = () => {
        navigate("/explore");
    };

    // handle navigate to detail page
    const _handleNavigateToDetail = (product) => {
        navigate("detail", {
            state: {
                "portfolio_id": product.portfolio_id,
                "tenant_id": product.creator_id
            }
        })
    };

    // handle on search
    const _handleOnSearchClick = () => {
        setDataLoading(true);
        _getOrderList();
    };


    /////////////////////////////////
    //  API  -  Get order list data //
    const _getOrderList = async () => {
        let url = "portfolio/orders/list";
        let data = {
            "portfolio_type": "Basket",
            "sort_by": selectedSort,
            "order_status": selectedOrderStatusId?.length ? selectedOrderStatusId : "",
            "query": searchValue,
            "user_id": USER_DATA.unique_identifier,
        };

        const response = await APIService(url, data);
        if (response.status_code === 200) {
            setOrderList(response.data);
            initialRender.current = false;
        } else {
            setOrderList([]);
        }

        setDataLoading(false);
    };


    return (
        <>
            <TextButton name="Orders" type={2} labelClass="e-font-16-res" />
            <div className={`${initialRender.current === true ? "col-12" : "col-12 col-lg-10 col-xl-8"}  mt-4`}>
                <div className="card-box-shadow e-bg-white border-radius-16 p-3 min-vh-100 mb-5">

                    {/* Filters */}
                    {
                        initialRender.current === false &&
                        <div className='row flex-sm-nowrap margin-20px-bottom px-3 justify-content-between row-gap-3' >
                            {/* Sort */}
                            <div className="order-2 order-sm-1 col-6 col-sm-auto ps-0 position-relative">
                                <div className='e-padding-8px-tb-12px-lr-res d-flex justify-content-between gap-6px align-items-center e-border-0-5-ocean-blue e-bg-magnolia px-sm-3 padding-10px-tb border-radius-100 cursor-pointer'
                                    onClick={_handleSortToggle}>
                                    <p className='e-font-12-res mb-0 e-text-chinese-black e-alt-font-lato e-font-14 e-font-weight-600 line-height-20px'>
                                        Sort by
                                    </p>
                                    <Icon icon="drop-down-icon"
                                        color="#141514"
                                        className="d-flex"
                                        size="12px" />
                                </div>

                                {
                                    toggleSort &&
                                    <>
                                        <OverLay isOpen={toggleSort} onClose={_handleCancelSort} />
                                        <OrdersSortModal options={SORT_OPTIONS}
                                            selected={selectedSort}
                                            handleSelect={_handleSelectSort}
                                            handleClear={_handleClearSort}
                                            handleCancel={_handleCancelSort}
                                            handleApply={_handleApplySort} />
                                    </>
                                }
                            </div>

                            {/* Search input */}
                            <CustomSearchInput placeholder="Search with product names"
                                className=""
                                wrapperClassName="order-1 order-sm-2 px-0 col-12 col-sm-auto flex-fill offset-md-2"
                                type="text"
                                require
                                search={true}
                                value={searchValue}
                                handleOnSearch={_handleOnSearchClick}
                                handleChange={_handleSearch} />

                            {/* Filter */}
                            <div className="order-3 col-6 col-sm-auto pe-0 position-relative">
                                <div className='e-padding-8px-tb-12px-lr-res d-flex gap-6px align-items-center e-border-0-5-ocean-blue e-bg-magnolia px-sm-3 padding-10px-tb border-radius-100 cursor-pointer'
                                    onClick={_handleToggleFilter}>
                                    <div className='d-flex position-relative'>
                                        <img src={require("../../Assets/Images/Other/filter.svg").default}
                                            width={18} />
                                    </div>
                                    <p className='e-font-12-res mb-0 e-text-chinese-black e-alt-font-lato e-font-14 e-font-weight-600 line-height-20px'>
                                        Filter
                                    </p>
                                </div>

                                {/* Filter modal */}
                                {
                                    toggleFilter &&
                                    <>
                                        <Overlay isOpen={toggleFilter}
                                            onClose={_handleCancelFilter} />
                                        <OrderFilterModal
                                            orderStatus={ORDER_STATUS}
                                            paymentStatus={PAYMENT_STATUS}
                                            orderType={ORDER_TYPE}
                                            selectedOrderStatusId={selectedOrderStatusId}
                                            selectedOrderTypeId={selectedOrderTypeId}
                                            selectedPaymentStatusId={selectedPaymentStatusId}
                                            handleSelectOrderType={_handleSelectOrderType}
                                            handleSelectOrderStatus={_handleSelectOrderStatus}
                                            handleSelectPaymentStatus={_handleSelectPaymentStatus}
                                            handleClear={_handleClearFilter}
                                            handleCancel={_handleCancelFilter}
                                            handleApply={_handleApplyFilter}
                                        />
                                    </>
                                }

                            </div>
                        </div>
                    }

                    {
                        dataLoading ?
                            <Loader wrapperClassName="min-vh-100 margin-100px-top" />
                            :
                            orderList && orderList.length === 0 ?
                                <EmptyScreen
                                    type={initialRender.current === false ? 2 : 1}
                                    emptyScreenImage={EmptyOrder}
                                    description={initialRender.current === false ? "No orders found. Try different filters" : "Haven't Invested Yet? Start today and take control of your Financial future."}
                                    btnLabel={initialRender.current === false ? "" : "Start Investing"}
                                    onClick={_handleStartInvesting} />

                                :
                                <div className='row row-gap-4 pb-4'>
                                    {
                                        orderList.map((product, key) => (
                                            <div key={key}
                                                className='col-md-10'>
                                                <AlphaBasketCard product={product}
                                                    type={2}
                                                    onClick={() => _handleNavigateToDetail(product)} />
                                            </div>
                                        ))
                                    }
                                </div>
                    }
                </div>
            </div >
        </>
    )
}

export default OrderListing;